/**
 * Money formatting — the ONE formatter (ADR-0003).
 *
 * Every money figure renders in the currency its API response states
 * (`currency`). There is no default currency: a null currency means the
 * response is bare (several currencies with no rate, or unknown), so the
 * figure renders as a plain number, never under an assumed "$". A null value
 * is the backend's "can't show this" and renders "—", never 0.
 */

/** The currency fields a money-carrying API response returns. */
export interface CurrencyScope {
  currency?: string | null;
  currency_mixed?: boolean | null;
  reporting_currency?: string | null;
  currency_unavailable?: boolean | null;
}

/** Every figure renders in this locale unless a caller passes `locale` (matches the existing UI's Intl sites). */
export const APP_LOCALE = "en-US";

type Opts = { decimals?: number; locale?: string };

function code(currency: string | null | undefined): string {
  return (currency || "").trim().toUpperCase();
}

function format(value: number, currency: string | null | undefined, locale: string | undefined,
                options: Intl.NumberFormatOptions): string {
  locale ??= APP_LOCALE;
  // A value that rounds to zero at the shown digits (-0.4, -0) renders "€0", never "-€0".
  // Not `signDisplay: "negative"`: Firefox 114-115 (in Vite's default build target) throw on it.
  // Compact only rounds to zero below 1, where it shows maximumFractionDigits unscaled.
  const shown = Number(value.toFixed(options.maximumFractionDigits ?? 0));
  const v = shown === 0 ? 0 : value;
  const c = code(currency);
  if (!c) return new Intl.NumberFormat(locale, options).format(v);
  try {
    return new Intl.NumberFormat(locale, { ...options, style: "currency", currency: c }).format(v);
  } catch {
    // An unknown code is still stated, never swapped for a symbol it isn't.
    return `${c} ${new Intl.NumberFormat(locale, options).format(v)}`;
  }
}

/** A money figure in `currency` ("€480,669"); bare number when the currency is null; "—" for no value. */
export function formatMoney(
  value: number | null | undefined,
  currency: string | null | undefined,
  opts: Opts = {},
): string {
  if (typeof value !== "number" || !Number.isFinite(value)) return "—";
  const digits = opts.decimals ?? 0;
  return format(value, currency, opts.locale, { minimumFractionDigits: digits, maximumFractionDigits: digits });
}

/** Compact form for tiles and axis ticks ("€1.3M"), with the same null rules as formatMoney. */
export function formatMoneyCompact(
  value: number | null | undefined,
  currency: string | null | undefined,
  opts: { locale?: string } = {},
): string {
  if (typeof value !== "number" || !Number.isFinite(value)) return "—";
  return format(value, currency, opts.locale, {
    notation: "compact",
    // Explicit min: older engines throw when max is below a currency's default min (2),
    // and the catch would then print "EUR 1.3M".
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
  });
}

/**
 * The ⓘ tooltip text for a section whose money is converted, can't be put in
 * one currency, or couldn't be confirmed (owner decision D5); null when there
 * is nothing unusual to say (one native currency, or nothing known).
 */
export function moneyScopeNote(scope: CurrencyScope | null | undefined): string | null {
  if (!scope) return null;
  const shown = code(scope.currency);
  if (shown) return scope.currency_mixed ? `Shown in ${shown}` : null;
  if (scope.currency_unavailable) return "Not available right now";
  if (scope.currency_mixed) return "Not available in one currency";
  return null;
}

/**
 * A section's ⓘ text: its scope note, dropped when `page` (the page title's
 * scope) already states the same scope (R27c), plus " · Some customers not
 * included" when customers were left out because none of their orders has a
 * rate (D5: the tooltip says it, never a sentence on screen); null when there
 * is nothing to say.
 */
export function scopeTooltip(
  scope: CurrencyScope | null | undefined,
  opts: { page?: CurrencyScope | null; customersWithoutRate?: number | null } = {},
): string | null {
  const note = sameScope(scope, opts.page) ? null : moneyScopeNote(scope);
  if (!((opts.customersWithoutRate ?? 0) > 0)) return note;
  return note ? `${note} · Some customers not included` : "Some customers not included";
}

/**
 * True when a response's money can't be shown as an amount: several currencies
 * with no single basis, or the currency couldn't be confirmed (owner decision
 * D6). Such money renders "—" and charts of it show their empty state.
 */
export function moneyHidden(scope: CurrencyScope | null | undefined): boolean {
  if (!scope || code(scope.currency)) return false;
  return Boolean(scope.currency_mixed || scope.currency_unavailable);
}

/** formatMoney in a response's currency; "—" when that response's money is hidden (D6). */
export function formatScopedMoney(
  value: number | null | undefined,
  scope: CurrencyScope | null | undefined,
  opts: Opts = {},
): string {
  return moneyHidden(scope) ? "—" : formatMoney(value, scope?.currency, opts);
}

/** formatMoneyCompact in a response's currency; "—" when that response's money is hidden (D6). */
export function formatScopedMoneyCompact(
  value: number | null | undefined,
  scope: CurrencyScope | null | undefined,
  opts: { locale?: string } = {},
): string {
  return moneyHidden(scope) ? "—" : formatMoneyCompact(value, scope?.currency, opts);
}

/**
 * A response's money, compact from 1,000 either side of zero ("€12.3K") and
 * exact below that (`decimals` applies there only); "—" when hidden (D6) or no value.
 */
export function formatScopedMoneyAuto(
  value: number | null | undefined,
  scope: CurrencyScope | null | undefined,
  opts: Opts = {},
): string {
  if (typeof value === "number" && Math.abs(value) >= 1_000) {
    return formatScopedMoneyCompact(value, scope, { locale: opts.locale });
  }
  return formatScopedMoney(value, scope, opts);
}

/** True when two responses' money can be compared or drawn on one axis (same stated currency). */
export function sameBasis(a: CurrencyScope | null | undefined, b: CurrencyScope | null | undefined): boolean {
  const ca = code(a?.currency);
  return Boolean(ca) && ca === code(b?.currency);
}

/**
 * True when two responses state the same currency scope (currency, mixed,
 * unavailable), so one ⓘ on the page covers both (R27c: one icon per distinct
 * scope per page). A missing scope is never the same as anything.
 */
export function sameScope(a: CurrencyScope | null | undefined, b: CurrencyScope | null | undefined): boolean {
  if (!a || !b) return false;
  return (
    code(a.currency) === code(b.currency) &&
    Boolean(a.currency_mixed) === Boolean(b.currency_mixed) &&
    Boolean(a.currency_unavailable) === Boolean(b.currency_unavailable)
  );
}
